/**
 * Command palette — Linear/Slack-style overlay opened with Cmd+K / Ctrl+K.
 * Searches commands (navigation, record creation) and navigates on Enter.
 * Keyboard accessible: arrow keys move, Enter runs, Esc closes, focus
 * returns to the trigger.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NAV_ENTRIES } from "./nav-data";
import { paletteCommands, visibleNav } from "./nav-model";
import { Icon } from "@/components/icons";
import { useAuthStore } from "@/lib/auth/store";
import { navLabel, useTranslation } from "@/lib/i18n";
import { NotAvailableTag } from "./NotAvailable";

export function CommandPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const commands = useMemo(() => {
    const q = query.trim().toLowerCase();
    // What the sidebar offers, each page once. Shown in the active language;
    // found by either that or the English label, so a command learnt in one
    // language still works in the other, and by its section's name, so
    // "CRM" lists every CRM page.
    const all = paletteCommands(visibleNav(hasPermission, NAV_ENTRIES)).map(
      (c) => ({
        path: c.path,
        label: navLabel(t, c.label),
        notAvailable: c.notAvailable,
        section: c.section ? navLabel(t, c.section) : undefined,
        terms: c.terms
          .flatMap((name) => [name, navLabel(t, name)])
          .map((term) => term.toLowerCase()),
      }),
    );
    return q
      ? all.filter((c) => c.terms.some((term) => term.includes(q)))
      : all.slice(0, 12);
  }, [query, t, hasPermission]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => setSelected(0), [query]);

  if (!open) return null;

  const run = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("shell.palette.label")}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelected((s) => Math.min(s + 1, commands.length - 1));
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelected((s) => Math.max(s - 1, 0));
        }
        if (e.key === "Enter" && commands[selected]) {
          e.preventDefault();
          run(commands[selected]!.path);
        }
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(15, 23, 42, 0.45)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "14vh",
      }}
    >
      <div
        style={{
          width: 560,
          maxWidth: "92vw",
          background: "var(--atlas-surface)",
          borderRadius: 12,
          boxShadow: "0 24px 48px rgba(0,0,0,0.25)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "14px 16px",
            borderBottom: "1px solid var(--atlas-border)",
          }}
        >
          <Icon name="search" size={18} />
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded="true"
            aria-activedescendant={
              commands[selected] ? `cmd-${selected}` : undefined
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("shell.palette.placeholder")}
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontSize: 15,
              background: "transparent",
            }}
          />
          <kbd style={{ fontSize: 12, color: "var(--atlas-text-muted)" }}>
            Esc
          </kbd>
        </div>
        <ul
          role="listbox"
          style={{
            listStyle: "none",
            margin: 0,
            padding: 8,
            maxHeight: 320,
            overflowY: "auto",
          }}
        >
          {commands.length === 0 && (
            <li style={{ padding: 16, color: "var(--atlas-text-muted)" }}>
              {t("shell.palette.noResults", { query })}
            </li>
          )}
          {commands.map((cmd, i) => (
            <li
              id={`cmd-${i}`}
              key={cmd.path}
              role="option"
              aria-selected={selected === i}
              onMouseEnter={() => setSelected(i)}
              onClick={() => run(cmd.path)}
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 8,
                padding: "8px 12px",
                borderRadius: 8,
                cursor: "pointer",
                background: selected === i ? "var(--atlas-primary)" : undefined,
                color: selected === i ? "#fff" : undefined,
              }}
            >
              <span>{cmd.label}</span>
              {/* A page that runs nothing yet says so beside its name. */}
              {cmd.notAvailable && " "}
              {cmd.notAvailable && <NotAvailableTag />}
              {/* Which section: two pages can share a name (Supplier Invoices
                  in Finance and in Procurement). The space keeps the two
                  apart in the option's accessible name. */}
              {cmd.section && " "}
              {cmd.section && (
                <span
                  style={{
                    marginInlineStart: "auto",
                    fontSize: 12,
                    opacity: 0.75,
                  }}
                >
                  {cmd.section}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
